// src/api/admin.ts
// Admin & Superadmin endpoints (auth required)

import { V1, authFetch } from "api/client";

export interface AdminWhoAmI {
  is_admin: boolean;
  is_superadmin: boolean;
  email: string;
}

export interface AdminPackage {
  id: number;
  name: string;
  slug: string;
  short_description: string;
  long_description?: string | null;
  price_cents: number;
  currency: string;
  status: "draft" | "published" | "archived";
  created_date: string;
  last_updated_date: string;
  lock_version: number;
}

export interface AdminClinic {
  id: number;
  name: string;
  slug: string;
  short_description: string;
  long_description?: string | null;
  city?: string | null;
  country?: string | null;
  website_url?: string | null;
  phone?: string | null;
  email?: string | null;
  status: "draft" | "published" | "archived";
  created_date: string;
  last_updated_date: string;
  lock_version: number;
}

/** Superadmin user search row */
export interface UserRow {
  id: number;
  email: string;
  display_name?: string | null;
  is_admin: boolean;
  is_superadmin: boolean;
  created_date: string;
  last_login_date?: string | null;
}

export interface PagedUsersOut {
  items: UserRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface AdminGrantIn {
  email: string;
}

export interface AdminGrantOut {
  user_id: number;
  email: string;
  is_admin: true;
}

export async function adminWhoAmI(): Promise<AdminWhoAmI> {
  return authFetch<AdminWhoAmI>(`${V1}/admin/whoami`, { method: "GET" });
}

// ------------------------ Packages ------------------------
export async function adminListPackages(): Promise<AdminPackage[]> {
  return authFetch<AdminPackage[]>(`${V1}/admin/packages`, { method: "GET" });
}

export async function adminCreatePackage(payload: {
  name: string;
  slug: string;
  short_description: string;
  long_description?: string | null;
  price_cents: number;
  currency: string;
}): Promise<AdminPackage> {
  return authFetch<AdminPackage>(`${V1}/admin/packages`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdatePackage(
  id: number,
  payload: Partial<{
    name: string;
    short_description: string;
    long_description?: string | null;
    price_cents: number;
    currency: string;
    expected_lock_version: number;
  }>
): Promise<AdminPackage> {
  return authFetch<AdminPackage>(`${V1}/admin/packages/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function adminPublishPackage(
  id: number,
  expected_lock_version: number
): Promise<AdminPackage> {
  return authFetch<AdminPackage>(
    `${V1}/admin/packages/${id}/publish?expected_lock_version=${expected_lock_version}`,
    { method: "POST" }
  );
}

export async function adminArchivePackage(
  id: number,
  expected_lock_version: number
): Promise<AdminPackage> {
  return authFetch<AdminPackage>(
    `${V1}/admin/packages/${id}/archive?expected_lock_version=${expected_lock_version}`,
    { method: "POST" }
  );
}

// ------------------------ Clinics ------------------------
export async function adminListClinics(): Promise<AdminClinic[]> {
  return authFetch<AdminClinic[]>(`${V1}/admin/clinics`, { method: "GET" });
}

export async function adminCreateClinic(payload: {
  name: string;
  slug: string;
  short_description: string;
  long_description?: string | null;
  city?: string | null;
  country?: string | null;
  website_url?: string | null;
  phone?: string | null;
  email?: string | null;
}): Promise<AdminClinic> {
  return authFetch<AdminClinic>(`${V1}/admin/clinics`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function adminUpdateClinic(
  id: number,
  payload: Partial<{
    name: string;
    short_description: string;
    long_description?: string | null;
    city?: string | null;
    country?: string | null;
    website_url?: string | null;
    phone?: string | null;
    email?: string | null;
    expected_lock_version: number;
  }>
): Promise<AdminClinic> {
  return authFetch<AdminClinic>(`${V1}/admin/clinics/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function adminPublishClinic(
  id: number,
  expected_lock_version: number
): Promise<AdminClinic> {
  return authFetch<AdminClinic>(
    `${V1}/admin/clinics/${id}/publish?expected_lock_version=${expected_lock_version}`,
    { method: "POST" }
  );
}

export async function adminArchiveClinic(
  id: number,
  expected_lock_version: number
): Promise<AdminClinic> {
  return authFetch<AdminClinic>(
    `${V1}/admin/clinics/${id}/archive?expected_lock_version=${expected_lock_version}`,
    { method: "POST" }
  );
}

// ------------------------ Superadmin ------------------------
export async function superadminSearchUsers(
  search: string,
  page: number,
  pageSize: number
): Promise<PagedUsersOut> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  params.set("page", String(page));
  params.set("page_size", String(pageSize));
  return authFetch<PagedUsersOut>(
    `${V1}/superadmin/users?${params.toString()}`,
    { method: "GET" }
  );
}

export async function superadminGrantAdmin(
  email: string
): Promise<AdminGrantOut> {
  const payload: AdminGrantIn = { email };
  return authFetch<AdminGrantOut>(`${V1}/superadmin/admins`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function superadminRevokeAdmin(userId: number): Promise<void> {
  await authFetch<void>(`${V1}/superadmin/admins/${userId}`, {
    method: "DELETE",
  });
}

/* ======================== Notifications ======================== */

export type NotificationStatus = "queued" | "sending" | "sent" | "failed" | "acknowledged";

export interface NotificationOut {
  id: number;
  kind: string;
  status: NotificationStatus;
  attempt_count: number;
  last_error?: string | null;
  created_date: string;
  // On a row stuck in 'sending' this is when it was claimed.
  last_updated_date?: string | null;
  queued_at?: string | null;
  sent_at?: string | null;
  acknowledged_at?: string | null;
  lock_version: number;
  order_id?: number | null;
  user_email?: string | null;
  clinic_name?: string | null;
  payload?: any;
}

export interface PagedNotifications {
  items: NotificationOut[];
  total: number;
  page: number;
  page_size: number;
}

export async function adminListNotifications(params: {
  status?: string;
  page?: number;
  page_size?: number;
  query?: string;
}): Promise<PagedNotifications> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.page) qs.set("page", String(params.page));
  if (params.page_size) qs.set("page_size", String(params.page_size));
  if (params.query) qs.set("q", params.query);
  const url = `${V1}/admin/notifications${
    qs.toString() ? `?${qs.toString()}` : ""
  }`;
  return authFetch<PagedNotifications>(url, { method: "GET" });
}

export async function adminGetNotification(
  id: number
): Promise<NotificationOut> {
  return authFetch<NotificationOut>(`${V1}/admin/notifications/${id}`, {
    method: "GET",
  });
}

export async function adminRetryNotification(
  id: number,
  expected_lock_version?: number
): Promise<NotificationOut> {
  const body = expected_lock_version != null ? { expected_lock_version } : {};
  return authFetch<NotificationOut>(
    `${V1}/admin/notifications/${id}/retry`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
}

export async function adminAcknowledgeNotification(
  id: number,
  expected_lock_version?: number
): Promise<NotificationOut> {
  const body = expected_lock_version != null ? { expected_lock_version } : {};
  return authFetch<NotificationOut>(
    `${V1}/admin/notifications/${id}/acknowledge`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
}

/* ======================== Admin Order Results ======================== */

export interface AdminOrderResult {
  id: number;
  order_id: number;
  file_name: string;
  mime_type?: string | null;
  size_bytes?: number | null;
  status: "draft" | "published" | "archived";
  lock_version: number;
  created_date: string;
  last_updated_date: string;
  published_at?: string | null;
  file_url?: string | null;
}

/** 📄 List results for an order (admin only) */
export async function listAdminOrderResults(
  orderId: number
): Promise<AdminOrderResult[]> {
  const res = await authFetch<any>(
    `${V1}/admin/orders/${orderId}/results`,
    { method: "GET" }
  );
  if (Array.isArray(res)) return res as AdminOrderResult[];
  if (Array.isArray(res?.items)) return res.items as AdminOrderResult[];
  return [];
}

/** 📤 Upload a new DOCX result (admin) */
export async function uploadAdminOrderResult(
  orderId: number,
  formData: FormData
): Promise<AdminOrderResult> {
  return authFetch<AdminOrderResult>(
    `${V1}/admin/orders/${orderId}/results`,
    {
      method: "POST",
      body: formData,
    }
  );
}

/** 🚀 Publish a result */
export async function publishOrderResult(
  orderId: number,
  resultId: number,
  payload: {
    expected_lock_version: number;
    mark_order_resulted?: boolean;
    order_expected_lock_version?: number | null;
  }
): Promise<AdminOrderResult> {
  return authFetch<AdminOrderResult>(
    `${V1}/admin/orders/${orderId}/results/${resultId}/publish`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

/** 🗄️ Archive / Unpublish a result (JSON body style, OCC enforced) */
export async function archiveOrderResult(
  orderId: number,
  resultId: number,
  payload: { expected_lock_version: number }
): Promise<AdminOrderResult> {
  return authFetch<AdminOrderResult>(
    `${V1}/admin/orders/${orderId}/results/${resultId}/archive`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

